import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';

interface InviteHotelAdminProps {
  open: boolean;
  onClose: () => void;
  hotelId: string;
  onInvited?: () => void;
}

interface HotelSummary {
  documentId: string;
  name: string;
  longName: string;
}

export default function InviteHotelAdmin({ open, onClose, hotelId, onInvited }: InviteHotelAdminProps) {
  const [hotel, setHotel] = useState<HotelSummary | null>(null);
  const [loadingHotel, setLoadingHotel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const { setSnackbarInput } = useApp();

  const isFormComplete = useMemo(() => {
    return firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().includes('@');
  }, [firstName, lastName, email]);

  useEffect(() => {
    if (!open || !hotelId) {
      return;
    }

    const fetchHotel = async () => {
      try {
        setLoadingHotel(true);
        setFormError('');

        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${APIHOST}/api/hotels/${hotelId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load hotel (${response.status})`);
        }

        const payload = await response.json();
        setHotel(payload?.data || null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load hotel';
        setFormError(message);
      } finally {
        setLoadingHotel(false);
      }
    };

    fetchHotel();
  }, [open, hotelId]);

  const handleInvite = async () => {
    if (!isFormComplete) {
      setFormError('First name, last name, and a valid email are required.');
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');

      const normalizedEmail = email.trim().toLowerCase();
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      const usernameBase = normalizedEmail.split('@')[0]?.replace(/[^a-zA-Z0-9._-]/g, '') || 'hoteladmin';
      const registerPayload = {
        username: `${usernameBase}.${Date.now()}`,
        email: normalizedEmail,
        password: `Temp!${Math.random().toString(36).slice(-10)}Aa1`,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      };

      const registerResponse = await fetch(`${APIHOST}/api/auth/local/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });

      if (!registerResponse.ok) {
        let registerError = `Failed to register user (${registerResponse.status})`;
        try {
          const errorData = await registerResponse.json();
          registerError = errorData?.error?.message || registerError;
        } catch {
          const errorText = await registerResponse.text();
          registerError = errorText || registerError;
        }
        throw new Error(registerError);
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Associate the new user with the hotel and send invitation email
      const response = await fetch(`${APIHOST}/api/invite/hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to invite hotel admin (${response.status})`);
      }

      setSnackbarInput({
        message: 'Invitation sent successfully',
        severity: 'success',
      });

      setFirstName('');
      setLastName('');
      setEmail('');
      if (onInvited) {
        onInvited();
      }
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to invite hotel admin';
      setFormError(message);
      setSnackbarInput({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite Hotel Administrator</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {loadingHotel ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hotel: {hotel?.longName || hotel?.name || hotelId}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  This will create a new user account for the invitee and send them an email with instructions to set their password. Once they create a new password, they will be able to access the hotel admin for this hotel.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  This is for new users only. If the user already has an account with the provided email, just add them through the hotel admins search box.
                </Alert>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleInvite} variant="contained" disabled={!isFormComplete || submitting || loadingHotel}>
          {submitting ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}